import { useCallback, useEffect, useState, useRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { FormControl, Grid, InputLabel, MenuItem, Paper, Select, IconButton, Tooltip, FormControlLabel, Checkbox, Button } from '@mui/material';
import { useNavigate } from 'react-router';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import InfoIcon from '@mui/icons-material/Info';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import b64toBlob from 'b64-to-blob';

import { useWidgetContext } from '../../../hooks/widgetContext';
import { configureStackedBarChart } from '../../ChartConfiguration/ChartConfiguration';
import { fetchIndividualProviderVisitPerformance, exportIndividualProviderVisitPerformance, MenuProps, fetchProvidersByRoles } from '../WidgetService';
import EngagementSummary from '../../../Models/Widget/EngagementSummary.model';
import { TimePeriodFilterEnum } from '../../../Enum/TimePeriodFilterEnum';
import { getDateFilter } from '../../HedisMetrics/HedisMetricService';
import '../Widgets.css';

const IndividualProviderVisitPerformance = () => {
    const history = useNavigate();
    const {
        individualEngagementDateFilter,
        setIndividualEngagementDateFilter,
        individualEngagementFromDate,
        setIndividualEngagementFromDate,
        individualEngagementToDate,
        setIndividualEngagementToDate,
        individualEngagementSelectedProvider,
        setIndividualEngagementSelectedProvider,
        individualEngagementShowInActiveProviders,
        setIndividualEngagementShowInActiveProviders,
    } = useWidgetContext();
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [isGraphShow, setIsGraphShow] = useState<boolean>(false);
    const [engagementMemberSummary, setEngagementMemberSummary] = useState<EngagementSummary>();
    const [chartOptions, setChartOptions] = useState({});
    const [show, setShow] = useState(false);
    const ref = useRef(null);
    const [loadedPage, setLoadedPage] = useState(false);
    const [downLoading, setDownLoading] = useState<boolean>(false);
    const [roleBasedProviders, setRoleBasedProviders] = useState<any[]>([]);
    const [filteredProviders, setFilteredProviders] = useState<any[]>([]);
    const chartHeight = 430;

    const handleClick = useCallback(() => {
        setShow(prev => !prev);
    }, []);

    const dateFilterList = getDateFilter();

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }

        if (!isGraphShow) {
            setChartOptions(
                configureStackedBarChart(
                    'Engagement Summary',
                    'Engagement Count',
                    'Visits',
                    'Count',
                    [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                    [0],
                    'IndividualEngagement',
                    history,
                    '',
                    '',
                    chartHeight
                )
            );
        }

        const data = await fetchProvidersByRoles();
        setRoleBasedProviders(data);
        var filteredProvider = data.filter((pro: any) => pro.Role.RoleName !== 'Inactive');
        setFilteredProviders(filteredProvider);

        setIsFetched(true);
    }, [isFetched, setIsFetched, isGraphShow, history]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        if (roleBasedProviders?.length) {
            setLoadedPage(true);
        }
    }, [roleBasedProviders]);

    useEffect(() => {
        const shouldFetch = loadedPage && (individualEngagementDateFilter !== TimePeriodFilterEnum.Custom || (individualEngagementFromDate && individualEngagementToDate));

        if (!shouldFetch) {
            return;
        }

        const changeFilter = async () => {
            setIsGraphShow(false);
            const data = await fetchIndividualProviderVisitPerformance(
                individualEngagementSelectedProvider,
                individualEngagementDateFilter,
                individualEngagementFromDate && individualEngagementDateFilter === TimePeriodFilterEnum.Custom
                    ? new Date(individualEngagementFromDate).toISOString()
                    : '',
                individualEngagementToDate && individualEngagementDateFilter === TimePeriodFilterEnum.Custom
                    ? new Date(individualEngagementToDate).toISOString()
                    : '',
                individualEngagementShowInActiveProviders,
                false
            );
            setEngagementMemberSummary(data);
        };

        setChartOptions(
            configureStackedBarChart(
                'Engagement Summary',
                'Engagement Count',
                'Visits',
                'Count',
                [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                [0],
                'IndividualEngagement',
                history,
                '',
                '',
                chartHeight
            )
        );

        changeFilter();
    }, [
        individualEngagementSelectedProvider,
        individualEngagementDateFilter,
        individualEngagementFromDate,
        individualEngagementToDate,
        individualEngagementShowInActiveProviders,
        loadedPage,
        history,
    ]);

    useEffect(() => {
        if (!engagementMemberSummary) return;

        const inPersonData = { y: engagementMemberSummary.InPersonVisits, name: engagementMemberSummary.InPersonVisitPercentage };
        const allVisitsData = { y: engagementMemberSummary.TotalVisits, name: 100 };

        const hasNoData = engagementMemberSummary.TotalVisits === 0 && engagementMemberSummary.InPersonVisits === 0;

        if (hasNoData) {
            setChartOptions(
                configureStackedBarChart(
                    'Engagement Summary',
                    'Engagement Count',
                    'Visits',
                    'Count',
                    [{ name: '', data: [], showInLegend: false, title: { text: null } }],
                    [0],
                    'IndividualEngagement',
                    history,
                    '',
                    '',
                    chartHeight
                )
            );
        } else {
            const chartData = [
                { name: 'In-Person Visits', data: [inPersonData], color: '#4682B4', showInLegend: true },
                { name: 'All Visits', data: [allVisitsData], color: '#ff7d7d', showInLegend: true },
            ];
            setChartOptions(
                configureStackedBarChart(
                    'Engagement Chart',
                    '',
                    '',
                    'Count',
                    chartData,
                    [''],
                    'IndividualEngagement',
                    history,
                    engagementMemberSummary.AverageDays,
                    '',
                    chartHeight
                )
            );
        }

        setIsGraphShow(true);
    }, [engagementMemberSummary, history]);

    const changeFromDate = useCallback((date: any) => {
        setIndividualEngagementFromDate(date);
    }, [setIndividualEngagementFromDate]);

    const changeToDate = useCallback((date: any) => {
        setIndividualEngagementToDate(date);
    }, [setIndividualEngagementToDate]);

    const navigateToMembers = useCallback(() => {
        history('/provider/metricsummary/individualinpersonvisitsdetail');
    }, [history]);

    const toggleProviderHandler = useCallback((isChecked: boolean) => {
        setIndividualEngagementShowInActiveProviders(isChecked);
        setIndividualEngagementSelectedProvider('All');
        if (isChecked) {
            var filteredProvider = roleBasedProviders;
            setFilteredProviders(filteredProvider);
        }
        else {
            var filteredProvider = roleBasedProviders.filter((pro: any) => pro.Role.RoleName !== 'Inactive');
            setFilteredProviders(filteredProvider);
        }
    }, [setIndividualEngagementShowInActiveProviders, setIndividualEngagementSelectedProvider, roleBasedProviders]);

    const exportDetail = async () => {
        setDownLoading(true);
        const data = await exportIndividualProviderVisitPerformance(
            individualEngagementSelectedProvider,
            individualEngagementDateFilter,
            individualEngagementFromDate && individualEngagementDateFilter === TimePeriodFilterEnum.Custom
                ? new Date(individualEngagementFromDate).toISOString()
                : '',
            individualEngagementToDate && individualEngagementDateFilter === TimePeriodFilterEnum.Custom
                ? new Date(individualEngagementToDate).toISOString()
                : '',
            individualEngagementShowInActiveProviders
        );

        const blob = b64toBlob(data.base64String, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const today = new Date();
        const month = today.getMonth() + 1; // getMonth() is zero-based
        const day = today.getDate();
        const dateInfo = [(month > 9 ? '' : '0') + month, (day > 9 ? '' : '0') + day, today.getFullYear()].join('');
        link.setAttribute('download', `IndividualProviderVisitPerformance - ${dateInfo}.xlsx`);
        link.click();
        link.remove();

        setDownLoading(false);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid size={{ xs: 12, lg: 6 }}>
                <Paper className="smartdashboard-widget" elevation={5} sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                    <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                        <Grid size={12} textAlign="center">
                            <Typography display="inline-block" variant="h6" marginRight={1} color="primary" ref={ref}>
                                <span className="pointer" onClick={navigateToMembers}>
                                    Individual Provider Visit Performance
                                </span>
                                <Tooltip
                                    open={show}
                                    title={
                                        <div>
                                            <Typography variant="caption">
                                                This widget will display the members that have had a completed visit within a given date range and will display out of all completed appointments,
                                                how many were in person, by phone or telemedicine. This widget helps you understand the types of engagement an individual or team is having with members.
                                            </Typography>
                                        </div>
                                    }
                                    PopperProps={{ style: { zIndex: 1000 } }}
                                >
                                    <IconButton onClick={handleClick}>
                                        <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                    </IconButton>
                                </Tooltip>
                            </Typography>
                        </Grid>
                        <Grid size={4}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Providers</InputLabel>
                                <Select
                                    value={individualEngagementSelectedProvider}
                                    label="Providers"
                                    name="Providers"
                                    onChange={(e) => setIndividualEngagementSelectedProvider(e.target.value)}
                                    MenuProps={MenuProps}
                                    renderValue={(selected) => {
                                        if (selected === 'All') {
                                            return 'All';
                                        }

                                        const provider = filteredProviders.find(
                                            (pro) => pro.Id === selected
                                        );

                                        return provider
                                            ? `${provider.LastName}, ${provider.FirstName}`
                                            : 'Provider';
                                    }}
                                >
                                    <MenuItem key="All" value="All">
                                        All
                                    </MenuItem>
                                    <FormControlLabel
                                        sx={{ pl: '12px', mb: '0px' }}
                                        control={
                                            <Checkbox
                                                checked={individualEngagementShowInActiveProviders}
                                                onChange={(e) => toggleProviderHandler(e.target.checked)}
                                            />
                                        }
                                        label="Show Inactive"
                                    />
                                    {filteredProviders.map((pro) => (
                                        <MenuItem key={pro.Id} value={pro.Id} sx={{ color: pro.Role.RoleName === 'Inactive' ? 'grey' : 'black' }}>
                                            {pro.LastName}, {pro.FirstName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={4}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Date Filter</InputLabel>
                                <Select
                                    value={individualEngagementDateFilter}
                                    label="Date Filter"
                                    name="Date"
                                    onChange={(e) => setIndividualEngagementDateFilter(parseInt(e.target.value as any))}
                                >
                                    {dateFilterList.map((date) => (
                                        <MenuItem key={date.Id} value={date.Id}>
                                            {date.Value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={1.7}>
                        </Grid>
                        <Grid size={1.5}>
                            <Button
                                style={{ minWidth: '120px', marginLeft: 'auto' }}
                                onClick={() => exportDetail()}
                                variant="contained"
                                color="success"
                                disabled={downLoading || engagementMemberSummary?.TotalVisits === 0}
                            >
                                {downLoading ? 'Exporting...' : 'Export'}
                            </Button>
                        </Grid>
                    </Grid>
                    {individualEngagementDateFilter === TimePeriodFilterEnum.Custom && (
                        <Grid container sx={{ mt: 2 }} spacing={2} paddingLeft={2} paddingRight={2}>
                            <Grid size={4}>
                                <DesktopDatePicker
                                    label="From Date"
                                    value={individualEngagementFromDate ?? null}
                                    minDate={new Date('2017-01-01')}
                                    onChange={changeFromDate}
                                    slotProps={{ textField: { fullWidth: true, size: "small" } }}
                                />
                            </Grid>
                            <Grid size={4}>
                                <DesktopDatePicker
                                    label="To Date"
                                    value={individualEngagementToDate ?? null}
                                    minDate={new Date('2017-01-01')}
                                    onChange={changeToDate}
                                    slotProps={{ textField: { fullWidth: true, size: "small" } }}
                                />
                            </Grid>
                        </Grid>
                    )}
                    <Grid container spacing={2} paddingLeft={2} paddingRight={2} marginTop={5}>
                        <Grid size={12} sx={{ position: 'relative' }}>
                            {!isGraphShow && (
                                <Box className="loader-center">
                                    <CircularProgress />
                                </Box>
                            )}
                            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                        </Grid>
                    </Grid>

                </Paper>
            </Grid>
        </LocalizationProvider>
    );
};

export default IndividualProviderVisitPerformance;
